import { useMemo, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { Card } from "shared/components/Card";
import { Loader } from "shared/components/Loader";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils";
import { useMedia } from "shared/providers/media-query/use-media";
import type { DateRangeParams, LeadJourneyResponse } from "api/crm-analytics";
import { AnalyticsEmptyState } from "../AnalyticsEmptyState";
import { useChartWidth } from "../modules/useChartWidth";
import {
  JOURNEY_EMPTY_DESCRIPTION,
  JOURNEY_HELP_TEXT,
  JOURNEY_TONES
} from "./constants";
import { buildJourneyGraph, type JourneySelection } from "./graph";
import { LeadJourneyModal } from "./LeadJourneyModal";
import { LeadJourneySankey } from "./LeadJourneySankey";
import { useDragScroll } from "./useDragScroll";
import type { UnitFilterParams } from "../UnitsFilter";

const LEGEND = [
  { label: "Progressed", color: JOURNEY_TONES.progressed },
  { label: "Stalled or lost", color: JOURNEY_TONES.stalled }
];

/**
 * Lead journey — the Sankey that replaces the Lead pipeline chart.
 *
 * Every lead created in the selected period, followed forward to the furthest
 * milestone it has reached. The API returns the finished node/link graph, so
 * this card only lays it out and wires the interactions; nothing about the
 * cohort, the milestones or the drop-off rules is decided here.
 */
export const LeadJourneyCard = ({
  data,
  isLoading,
  dateLabel,
  dateParams,
  filterParams,
  reserveActionGutter = false
}: {
  data: LeadJourneyResponse | undefined;
  isLoading: boolean;
  dateLabel: string;
  dateParams: DateRangeParams;
  filterParams?: UnitFilterParams;
  /** Leave room for a dashboard grid's delete button over the top-right corner. */
  reserveActionGutter?: boolean;
}) => {
  const { isMobile } = useMedia();
  const { ref: measureRef, width } = useChartWidth();
  const { scrollRef, canDrag, dragging, onPointerDown, onClickCapture } =
    useDragScroll();
  const [selected, setSelected] = useState<JourneySelection | null>(null);

  const graph = useMemo(() => (data ? buildJourneyGraph(data) : null), [data]);

  const hasData = (graph?.total ?? 0) > 0;
  const showChart = !isLoading && hasData && graph !== null;

  return (
    <Card className="h-full w-full p-6">
      <div
        className={cx(
          "flex flex-wrap items-start justify-between gap-4",
          reserveActionGutter && "pr-10"
        )}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <p className="text-base font-medium text-content-700">
              Lead journey
            </p>
            <Tooltip placement="right-start">
              <TooltipTrigger asChild>
                <span className="cursor-pointer text-content-300">
                  <HugeiconsIcon icon={InformationCircleIcon} size={16} />
                </span>
              </TooltipTrigger>
              <TooltipContent
                className={cx(
                  "w-full rounded-lg bg-background-1000 p-4",
                  isMobile ? "max-w-[400px]" : "max-w-[659px]"
                )}
              >
                <p className="text-sm font-medium text-content-1000">
                  {JOURNEY_HELP_TEXT}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <p className="text-sm font-medium text-content-300">{dateLabel}</p>
        </div>

        <div className="flex items-center gap-4">
          {LEGEND.map((item) => (
            <span
              key={item.label}
              className="flex items-center gap-2 text-sm font-medium text-content-300"
            >
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              {item.label}
            </span>
          ))}
        </div>
      </div>

      <Show if={isLoading}>
        <div className="flex min-h-[280px] items-center justify-center">
          <Loader size={24} />
        </div>
      </Show>

      <Show if={!isLoading && !hasData}>
        <AnalyticsEmptyState description={JOURNEY_EMPTY_DESCRIPTION} />
      </Show>

      <Show if={showChart}>
        <div
          ref={measureRef}
          className={cx(
            "mt-4 select-none",
            canDrag && (dragging ? "cursor-grabbing" : "cursor-grab")
          )}
          onPointerDown={onPointerDown}
          onClickCapture={onClickCapture}
        >
          <div ref={scrollRef} className="overflow-x-auto pb-3">
            {/* Measured width drives the layout, so the chart is only rendered
                once the container has been measured. */}
            <Show if={width > 0 && graph !== null}>
              <LeadJourneySankey
                graph={graph!}
                width={width}
                onSelect={setSelected}
              />
            </Show>
          </div>
        </div>
      </Show>

      <LeadJourneyModal
        selection={selected}
        onClose={() => setSelected(null)}
        dateParams={dateParams}
        filterParams={filterParams}
      />
    </Card>
  );
};
